import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getUserPositions,
  getUserHistoricalPositions,
  getUserPositionStats,
  sellUserPosition,
} from '../utils/api';
import { useI18n } from '../i18n/useI18n';
import { InfoTooltip } from '../components/InfoTooltip';
import { PageLoading } from '../components/LoadingSpinner';
import { Modal } from '../components/Modal';
import './Positions.css';

/**
 * 持仓页面组件
 */
export default function Positions({ pushToast }) {
  const { isAuthenticated, userId } = useAuth();
  const { t, lang } = useI18n();
  const locale = lang === 'zh' ? 'zh-CN' : 'en-US';
  const notify = pushToast || (() => {});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [positionsData, setPositionsData] = useState(null);
  const [historicalData, setHistoricalData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [activeWallet, setActiveWallet] = useState(0);
  const [positionFilter, setPositionFilter] = useState('all');
  const [sellLoadingId, setSellLoadingId] = useState(null);
  const [sellConfirmOpen, setSellConfirmOpen] = useState(false);
  const [sellTarget, setSellTarget] = useState(null);
  const walletSelectionTouchedRef = useRef(false);

  useEffect(() => {
    if (userId) {
      loadPositions();
    } else {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    setPositionFilter('all');
  }, [activeWallet]);

  useEffect(() => {
    if (walletSelectionTouchedRef.current) return;
    if (!positionsData && !historicalData) return;

    const walletsList = positionsData?.wallets?.length
      ? positionsData.wallets
      : (historicalData?.wallets ?? []).map((w) => ({
          wallet_address: w.wallet_address,
          positions: [],
        }));

    if (!walletsList.length) return;

    const historicalCountByAddress = (address) =>
      historicalData?.wallets?.find((w) => w.wallet_address === address)?.positions?.length ?? 0;

    let bestIndex = 0;
    let bestCount = -1;

    walletsList.forEach((wallet, idx) => {
      const currentCount = wallet.positions?.length ?? 0;
      const historicalCount = historicalCountByAddress(wallet.wallet_address);
      const totalCount = currentCount + historicalCount;
      if (totalCount > bestCount) {
        bestCount = totalCount;
        bestIndex = idx;
      }
    });

    const activeWalletData = walletsList[activeWallet];
    const activeCount = activeWalletData
      ? (activeWalletData.positions?.length ?? 0) + historicalCountByAddress(activeWalletData.wallet_address)
      : 0;

    const shouldAutoSelect =
      activeWallet >= walletsList.length || (activeCount === 0 && bestCount > 0 && bestIndex !== activeWallet);

    if (shouldAutoSelect) {
      setActiveWallet(bestIndex);
    }
  }, [positionsData, historicalData, activeWallet]);

  const loadPositions = async () => {
    try {
      setLoading(true);
      setError(null);
      const hadAnyData = Boolean(positionsData || historicalData);
      const [positionsResult, historicalResult, statsResult] = await Promise.allSettled([
        getUserPositions(userId),
        getUserHistoricalPositions(userId),
        getUserPositionStats(userId),
      ]);

      const positionsOk = positionsResult.status === 'fulfilled';
      const historicalOk = historicalResult.status === 'fulfilled';
      const statsOk = statsResult.status === 'fulfilled';

      if (positionsOk && positionsResult.value?.data) {
        setPositionsData(positionsResult.value.data);
      }
      if (historicalOk && historicalResult.value?.data) {
        setHistoricalData(historicalResult.value.data);
      }
      if (statsOk && statsResult.value?.data) {
        setStatsData(statsResult.value.data);
      }

      if (!positionsOk && !historicalOk && !hadAnyData) {
        const reason = positionsResult.reason || historicalResult.reason;
        setError(reason?.message || t('positions.fetchFailed'));
      }
    } catch (err) {
      setError(err.message || t('positions.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadPositions();
  };

  const getSellKey = (position) => `${position?.condition_id}:${position?.outcome}`;

  const handleSellPosition = (position) => {
    if (!userId || !position?.condition_id) return;
    setSellTarget(position);
    setSellConfirmOpen(true);
  };

  const handleCloseSellConfirm = () => {
    if (sellLoadingId) return;
    setSellConfirmOpen(false);
    setSellTarget(null);
  };

  const handleConfirmSell = async () => {
    if (!userId || !sellTarget?.condition_id) return;
    const sellKey = getSellKey(sellTarget);

    try {
      setSellLoadingId(sellKey);
      notify(t('positions.sellSubmitting'), 'info');

      const response = await sellUserPosition(userId, {
        condition_id: sellTarget.condition_id,
        outcome: sellTarget.outcome,
        wallet_address: sellTarget.wallet_address || currentWallet?.wallet_address,
        order_type: 'FAK',
      });

      if (response?.code === 0 && response?.data?.success) {
        notify(t('positions.sellSuccess'), 'success');
        setSellConfirmOpen(false);
        setSellTarget(null);
        loadPositions();
      } else {
        const errMsg = response?.code !== 0
          ? response?.msg
          : response?.data?.error || response?.data?.message;
        notify(errMsg || t('positions.sellFailed'), 'error');
      }
    } catch (err) {
      notify(err.message || t('positions.sellFailed'), 'error');
    } finally {
      setSellLoadingId(null);
    }
  };

  const formatUsd = (value, { withSign = false } = {}) => {
    const num = Number(value ?? 0);
    if (Number.isNaN(num)) {
      return '$0.00';
    }
    if (withSign) {
      if (num > 0) return `+$${num.toFixed(2)}`;
      if (num < 0) return `-$${Math.abs(num).toFixed(2)}`;
    }
    return `$${num.toFixed(2)}`;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return null;
    }
    const num = Number(value);
    const sign = num > 0 ? '+' : '';
    return `${sign}${num.toFixed(2)}%`;
  };

  const formatAddress = (address) => {
    if (!address) return '-';
    if (address.length <= 12) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  if (!isAuthenticated || !userId) {
    return (
      <div className="positions-container">
        <div className="positions-empty">
          <h2>{t('auth.loginRequiredTitle')}</h2>
          <p>{t('auth.loginRequiredDesc')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <PageLoading text={t('positions.loading')} />;
  }

  if (error) {
    return (
      <div className="positions-container">
        <div className="positions-error">
          <h2>❌ {t('positions.loadFailed')}</h2>
          <p>{error}</p>
          <button onClick={handleRefresh} className="btn-retry">
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  const currentTotalPositions = positionsData?.summary?.total_positions ?? 0;
  const historicalTotalPositions =
    historicalData?.wallets?.reduce((sum, w) => sum + (w.positions?.length ?? 0), 0) ?? 0;

  // 没有当前持仓 ≠ 没有历史（已结算）持仓：两者都为空才展示空状态
  if (currentTotalPositions === 0 && historicalTotalPositions === 0) {
    return (
      <section className="glass-lg w-full overflow-hidden">
        <div className="p-6 md:p-10 lg:p-10 flex flex-col gap-6">
          <div>
            <p className="uppercase tracking-[0.3em] text-[0.6rem] md:text-[0.65rem] text-text-muted">{t('positions.pageKicker')}</p>
            <h1 className="text-xl md:text-2xl lg:text-3xl my-1.5">{t('positions.emptyTitle')}</h1>
          </div>
          <div className="p-6 rounded-2xl bg-slate-900/70 border border-slate-600/55 text-slate-300">
            {t('positions.emptyHint')}
          </div>
        </div>
      </section>
    );
  }

  const wallets =
    positionsData?.wallets?.length
      ? positionsData.wallets
      : (historicalData?.wallets ?? []).map((w) => ({
          wallet_address: w.wallet_address,
          positions: [],
          is_trading_wallet: false,
          source: 'unknown',
        }));

  const summary = positionsData?.summary ?? {
    total_positions: 0,
    total_value: 0,
    total_pnl: 0,
    total_initial_value: 0,
  };

  const currentWallet = wallets[activeWallet] ?? wallets[0];
  // 当前持仓的浮动盈亏
  const currentPositionsPnl = summary?.total_pnl ?? 0;
  // 历史已实现盈亏（已赎回的持仓）
  const historicalRealizedPnl = statsData?.stats?.total_realized_pnl ?? 0;
  // 总盈亏 = 当前持仓盈亏（进行中+未赎回）+ 历史已实现盈亏（已赎回）
  const summaryTotalPnl = currentPositionsPnl + historicalRealizedPnl;
  const summaryInitialValue = summary?.total_initial_value ?? 0;
  const historicalInvested = statsData?.stats?.total_invested ?? 0;
  // 总投入 = 当前持仓投入 + 历史投入
  const totalInvested = summaryInitialValue + historicalInvested;
  const summaryPnlPercent = totalInvested
    ? (summaryTotalPnl / totalInvested) * 100
    : null;

  // 获取当前钱包的历史持仓数据
  const currentWalletHistorical = historicalData?.wallets?.find(
    w => w.wallet_address === currentWallet?.wallet_address
  );
  const settledPositionsFromHistory = currentWalletHistorical?.positions || [];

  // 解析结束日期用于排序（支持多种格式）
  const parseEndDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      // 尝试解析 ISO 格式
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date;
      }
      return null;
    } catch {
      return null;
    }
  };

  // 对当前持仓按 end_date 排序（最近的在前）
  const sortedCurrentPositions = [...(currentWallet?.positions ?? [])]
    .map((position) => ({
      ...position,
      wallet_address: position.wallet_address || currentWallet?.wallet_address,
    }))
    .sort((a, b) => {
      const dateA = parseEndDate(a.end_date);
      const dateB = parseEndDate(b.end_date);

    // 如果都没有日期，保持原顺序
    if (!dateA && !dateB) return 0;
    // 没有日期的排在后面
    if (!dateA) return 1;
    if (!dateB) return -1;
    // 按日期排序，最近的在前
      return dateA - dateB;
    });

  // 对历史持仓按 settled_at 排序（最近结算的在前）
  const sortedHistoricalPositions = [...settledPositionsFromHistory].sort((a, b) => {
    const dateA = a.settled_at ? new Date(a.settled_at) : null;
    const dateB = b.settled_at ? new Date(b.settled_at) : null;

    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    // 历史持仓：最近结算的在前（降序）
    return dateB - dateA;
  });

  // 合并当前持仓和历史持仓（已排序）
  const currentPositionsWithOrder = sortedCurrentPositions.map((position, index) => ({
    position,
    order: index,
    type: 'current',
  }));

  const historicalPositionsWithOrder = sortedHistoricalPositions.map((position, index) => ({
    position,
    order: sortedCurrentPositions.length + index,
    type: 'historical',
  }));

  const allPositions = [...currentPositionsWithOrder, ...historicalPositionsWithOrder];

  // 分类持仓 - 修正逻辑，确保所有持仓都能显示
  const activePositions = currentPositionsWithOrder.filter(
    ({ position }) => !position.redeemable && !position.market_status?.closed && !position.market_status?.resolved
  );
  const unredeemedPositions = currentPositionsWithOrder.filter(
    ({ position }) => position.redeemable  // 所有可赎回的持仓（未赎回）
  );
  const settledPositions = [
    ...historicalPositionsWithOrder  // 只有历史持仓是真正已结算的
  ];

  const totalPositions = allPositions.length;
  const activeCount = activePositions.length;
  const unredeemedCount = unredeemedPositions.length;
  const settledCount = settledPositions.length;

  // 已结算持仓统计（从历史数据获取）
  const settledStats = historicalData?.stats || {
    total_wins: 0,
    total_losses: 0,
    win_rate: null,
    total_realized_pnl: 0,
  };

  const filteredPositions = (() => {
    if (positionFilter === 'active') {
      return activePositions;
    }
    if (positionFilter === 'unredeemed') {
      return unredeemedPositions;
    }
    if (positionFilter === 'settled') {
      return settledPositions;
    }
    return allPositions;
  })();

  const pnlStats = {
    yesterday: statsData?.stats?.yesterday_pnl ?? 0,
    week: statsData?.stats?.week_pnl ?? 0,
    month: statsData?.stats?.month_pnl ?? 0,
    allTime: statsData?.stats?.total_realized_pnl ?? 0,
  };

  const sellTargetKey = sellTarget ? getSellKey(sellTarget) : null;
  const isSellingTarget = Boolean(sellTargetKey && sellLoadingId === sellTargetKey);
  const sellTargetPnl = sellTarget ? Number(sellTarget.pnl ?? 0) : 0;

  const renderPositionRow = (position, order, type) => {
    // 判断已结算持仓的输赢 - 只有历史持仓才是真正已结算
    const isSettled = type === 'historical';
    const marketResult = position.market_status?.market_result;
    const normalizeOutcome = (value) => (value ?? '').toString().trim().toUpperCase();
    const normalizedMarketResult = normalizeOutcome(marketResult);
    const userOutcome = normalizeOutcome(position.outcome);
    let winStatus = null; // 'win' | 'lose' | null

    if (isSettled && normalizedMarketResult) {
      winStatus = normalizedMarketResult === userOutcome ? 'win' : 'lose';
    }

    const pnlClass = position.pnl >= 0 ? 'profit' : 'loss';
    const isYes = position.outcome.toLowerCase() === 'yes';
    const outcomeColorClass = isYes ? 'text-emerald-400' : 'text-rose-400';
    const outcomeBgClass = isYes ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-rose-500/10 border-rose-500/20';
    const isActivePosition =
      type === 'current' && !position.redeemable && !position.market_status?.closed && !position.market_status?.resolved;

    const getStatusBadge = () => {
      if (position.redeemable) {
        return (
          <span className="text-[0.65rem] font-semibold px-2 py-0.5 rounded-full border border-amber-500/20 text-amber-400 bg-amber-500/10 whitespace-nowrap">
            {t('positions.statusUnredeemed')}
          </span>
        );
      } else if (position.market_status?.closed) {
        return (
          <span className="text-[0.65rem] font-semibold px-2 py-0.5 rounded-full border border-slate-500/20 text-slate-400 bg-slate-500/10 whitespace-nowrap">
            {t('positions.statusClosed')}
          </span>
        );
      } else if (position.market_status?.resolved) {
        return (
          <span className="text-[0.65rem] font-semibold px-2 py-0.5 rounded-full border border-emerald-500/20 text-emerald-400 bg-emerald-500/10 whitespace-nowrap">
            {t('positions.statusSettled')}
          </span>
        );
      }
      return (
        <span className="text-[0.65rem] font-semibold px-2 py-0.5 rounded-full border border-emerald-500/20 text-emerald-400 bg-emerald-500/10 whitespace-nowrap">
          {t('positions.statusActive')}
        </span>
      );
    };

    return (
      <div
        key={position.condition_id}
        className={`group relative flex items-center gap-3 px-3 py-3 rounded-xl border transition-all duration-200 min-w-max
          bg-slate-900/40 border-slate-700/50 hover:bg-slate-800/60 hover:border-slate-600/80
          ${winStatus === 'win' ? 'border-emerald-500/30 bg-emerald-900/10' : ''}
          ${winStatus === 'lose' ? 'border-rose-500/30 bg-rose-900/10' : ''}
        `}
      >
        {/* Index */}
        <span className="text-xs font-mono text-slate-500 font-medium w-6 shrink-0">#{order + 1}</span>

        {/* Outcome Badge */}
        <span className={`px-2 py-0.5 rounded-full border text-[0.65rem] font-bold tracking-wide uppercase flex items-center gap-1 shrink-0 ${outcomeBgClass} ${outcomeColorClass}`}>
          {position.outcome_emoji} {position.outcome}
        </span>

        {/* Title - Flexible with min-width for mobile scroll */}
        <h3 className="text-sm font-medium text-slate-200 leading-snug line-clamp-1 group-hover:text-white transition-colors flex-1 min-w-[120px]">
          {position.title || t('positions.marketUnknown')}
        </h3>

        {/* Stats - Fixed Width Columns */}
        <div className="flex items-center gap-4 sm:gap-6 shrink-0">
          {/* Position Size */}
          <div className="flex flex-col items-end w-14 sm:w-16">
            <span className="text-[0.6rem] text-slate-500 uppercase tracking-wider hidden sm:block">{t('positions.positionSize')}</span>
            <span className="text-sm text-slate-300 font-mono">{position.size.toFixed(2)}</span>
          </div>

          {/* Avg Price */}
          <div className="flex flex-col items-end w-14 sm:w-16">
            <span className="text-[0.6rem] text-slate-500 uppercase tracking-wider hidden sm:block">{t('positions.avgPrice')}</span>
            <span className="text-sm text-slate-300 font-mono">${position.avg_price?.toFixed(3) ?? '-'}</span>
          </div>

          {/* Current Price - Desktop only */}
          <div className="hidden md:flex flex-col items-end w-16">
            <span className="text-[0.6rem] text-slate-500 uppercase tracking-wider">{t('positions.currentPrice')}</span>
            <span className="text-sm text-slate-300 font-mono">${position.cur_price?.toFixed(3) ?? '-'}</span>
          </div>

          {/* Current Value */}
          <div className="flex flex-col items-end w-16 sm:w-20">
            <span className="text-[0.6rem] text-slate-500 uppercase tracking-wider hidden sm:block">{t('positions.currentValue')}</span>
            <span className="text-sm font-semibold text-slate-100">${position.value.toFixed(2)}</span>
          </div>

          {/* PnL */}
          <div className="flex flex-col items-end w-20 sm:w-24">
            <span className="text-[0.6rem] text-slate-500 uppercase tracking-wider hidden sm:block">
              {isSettled ? (winStatus === 'win' ? t('positions.pnlWin') : t('positions.pnlLoss')) : t('positions.pnlFloating')}
            </span>
            <div className={`text-sm font-semibold ${pnlClass === 'profit' ? 'text-emerald-400' : 'text-rose-400'}`}>
              {position.pnl >= 0 ? '+' : ''}${position.pnl.toFixed(2)}
              <span className={`text-[0.65rem] ml-1 opacity-80 hidden sm:inline`}>
                ({position.percent_pnl !== null ? `${position.percent_pnl >= 0 ? '+' : ''}${position.percent_pnl.toFixed(2)}%` : '-'})
              </span>
            </div>
          </div>
        </div>

        {/* Status & Win/Loss */}
        <div className="flex items-center gap-2 shrink-0 w-auto sm:w-28 justify-end">
          <span className="hidden sm:inline">{getStatusBadge()}</span>
          {winStatus && (
            <div className={`flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full ${winStatus === 'win' ? 'bg-emerald-500/20' : 'bg-rose-500/20'}`}>
              {winStatus === 'win' ? (
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-rose-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
          )}
        </div>

        {/* Sell Button */}
        <div className="shrink-0 w-16 sm:w-20">
          {isActivePosition ? (
            <button
              className="position-sell-btn w-full text-[0.65rem] sm:text-xs py-1 sm:py-1.5 px-1 sm:px-2"
              onClick={() => handleSellPosition(position)}
              disabled={sellLoadingId === getSellKey(position)}
            >
              {sellLoadingId === getSellKey(position)
                ? t('positions.sellPreparing')
                : t('positions.sellAction')}
            </button>
          ) : (
            <span className="font-mono text-[0.6rem] text-slate-600 opacity-60 hidden sm:inline" title={position.condition_id}>
              {position.condition_id.slice(0, 6)}...
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderHistoricalPositionRow = (position, order) => {
    const winStatus = position.is_win ? 'win' : (position.is_win === false ? 'lose' : null);
    const pnlClass = (position.realized_pnl ?? 0) >= 0 ? 'profit' : 'loss';
    const isYes = position.outcome.toLowerCase() === 'yes';
    const outcomeColorClass = isYes ? 'text-emerald-400' : 'text-rose-400';
    const outcomeBgClass = isYes ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-rose-500/10 border-rose-500/20';

    return (
      <div
        key={position.condition_id}
        className={`group relative flex items-center gap-3 px-3 py-3 rounded-xl border transition-all duration-200 min-w-max
          bg-slate-900/40 border-slate-700/50 hover:bg-slate-800/60 hover:border-slate-600/80
          ${winStatus === 'win' ? 'border-emerald-500/30 bg-emerald-900/10' : ''}
          ${winStatus === 'lose' ? 'border-rose-500/30 bg-rose-900/10' : ''}
        `}
      >
        {/* Index */}
        <span className="text-xs font-mono text-slate-500 font-medium w-6 shrink-0">#{order + 1}</span>

        {/* Outcome Badge */}
        <span className={`px-2 py-0.5 rounded-full border text-[0.65rem] font-bold tracking-wide uppercase flex items-center gap-1 shrink-0 ${outcomeBgClass} ${outcomeColorClass}`}>
          {position.outcome_emoji} {position.outcome}
        </span>

        {/* Title - Flexible with min-width for mobile scroll */}
        <h3 className="text-sm font-medium text-slate-200 leading-snug line-clamp-1 group-hover:text-white transition-colors flex-1 min-w-[120px]">
          {position.title || t('positions.marketUnknown')}
        </h3>

        {/* Stats - Fixed Width Columns */}
        <div className="flex items-center gap-4 sm:gap-6 shrink-0">
          {/* Position Size */}
          <div className="flex flex-col items-end w-14 sm:w-16">
            <span className="text-[0.6rem] text-slate-500 uppercase tracking-wider hidden sm:block">{t('positions.positionSize')}</span>
            <span className="text-sm text-slate-300 font-mono">{(position.size ?? 0).toFixed(2)}</span>
          </div>

          {/* Avg Price */}
          <div className="flex flex-col items-end w-14 sm:w-16">
            <span className="text-[0.6rem] text-slate-500 uppercase tracking-wider hidden sm:block">{t('positions.avgPrice')}</span>
            <span className="text-sm text-slate-300 font-mono">${(position.avg_price ?? 0).toFixed(3)}</span>
          </div>

          {/* Settled Value */}
          <div className="flex flex-col items-end w-16 sm:w-20">
            <span className="text-[0.6rem] text-slate-500 uppercase tracking-wider hidden sm:block">{t('positions.settledValue')}</span>
            <span className="text-sm font-semibold text-slate-100">${(position.final_value ?? 0).toFixed(2)}</span>
          </div>

          {/* Realized PnL */}
          <div className="flex flex-col items-end w-20 sm:w-24">
            <span className="text-[0.6rem] text-slate-500 uppercase tracking-wider hidden sm:block">
              {position.realized_pnl === 0 ? t('positions.settledResult') : winStatus === 'win' ? t('positions.realizedProfit') : t('positions.realizedLoss')}
            </span>
            <div className={`text-sm font-semibold ${pnlClass === 'profit' ? 'text-emerald-400' : 'text-rose-400'}`}>
              {position.realized_pnl === 0 ? '' : (position.realized_pnl ?? 0) >= 0 ? '+' : ''}${(position.realized_pnl ?? 0).toFixed(2)}
              <span className={`text-[0.65rem] ml-1 opacity-80 hidden sm:inline`}>
                ({position.percent_pnl !== null ? `${position.percent_pnl >= 0 ? '+' : ''}${position.percent_pnl.toFixed(2)}%` : '-'})
              </span>
            </div>
          </div>
        </div>

        {/* Status & Win/Loss */}
        <div className="flex items-center gap-2 shrink-0 w-auto sm:w-24 justify-end">
          <span className="hidden sm:inline text-[0.65rem] font-semibold px-2 py-0.5 rounded-full border border-purple-500/20 text-purple-400 bg-purple-500/10 whitespace-nowrap">
            {t('positions.statusSettled')}
          </span>
          {winStatus && (
            <div className={`flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full ${winStatus === 'win' ? 'bg-emerald-500/20' : 'bg-rose-500/20'}`}>
              {winStatus === 'win' ? (
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-rose-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
          )}
        </div>

        {/* Settled Time / ID */}
        <div className="shrink-0 w-16 sm:w-24 text-right">
          {position.settled_at ? (
            <span className="text-[0.65rem] text-slate-500">
              {new Date(position.settled_at).toLocaleDateString(locale)}
            </span>
          ) : (
            <span className="font-mono text-[0.6rem] text-slate-600 opacity-60 hidden sm:inline" title={position.condition_id}>
              {position.condition_id.slice(0, 6)}...
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="glass-lg w-full overflow-hidden min-h-[80vh]">
      <div className="p-4 sm:p-6 md:p-8 lg:p-10 flex flex-col gap-6 sm:gap-8">

        {/* Top Navigation & Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-6 mb-2">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-2 sm:gap-3">
              {t('positions.emptyTitle')}
              <span className="hidden sm:inline-flex px-3 py-1 text-xs font-bold bg-indigo-500/20 text-indigo-300 rounded-full border border-indigo-500/30">
                PORTFOLIO
              </span>
            </h1>
            <p className="text-slate-400 text-xs sm:text-sm mt-1">{t('positions.pageSubtitle')}</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={handleRefresh}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-slate-800/50 text-slate-300 hover:text-white hover:bg-slate-700/50 transition-all border border-slate-700/50 hover:border-slate-600 active:scale-95 text-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="hidden sm:inline">{t('positions.refresh')}</span>
              <span className="sm:hidden">{t('positions.refreshShort')}</span>
            </button>
          </div>
        </div>

        {/* Hero Dashboard */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-4">

          {/* Main Asset Card (Span 5) */}
          <div className="lg:col-span-5 relative p-4 sm:p-5 rounded-xl bg-gradient-to-br from-indigo-900/20 via-slate-900 to-slate-900 border border-indigo-500/10 overflow-hidden group flex flex-col justify-between">
            <div className="absolute top-0 right-0 -mr-8 -mt-8 w-40 h-40 bg-indigo-500/10 blur-[60px] rounded-full pointer-events-none"></div>

            <div className="relative z-10">
              <div className="flex items-center gap-1.5 text-indigo-300/80 font-medium text-[0.65rem] sm:text-xs uppercase tracking-wider mb-2">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400"></span>
                {t('positions.totalValue')}
              </div>
              <div className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
                {formatUsd(summary.total_value)}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-slate-400 text-[0.65rem] sm:text-xs uppercase tracking-wider mb-1 flex items-center gap-1">
                    {t('positions.totalUnrealizedPnl')}
                    <InfoTooltip content={t('positions.totalPnlTooltip')} />
                  </div>
                  <div className={`text-lg sm:text-xl font-bold ${summaryTotalPnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {formatUsd(summaryTotalPnl, { withSign: true })}
                  </div>
                  <div className={`text-[0.65rem] font-medium mt-1 inline-block px-1.5 py-0.5 rounded ${summaryPnlPercent >= 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'}`}>
                    ROI {summaryPnlPercent !== null ? formatPercent(summaryPnlPercent) : '-'}
                  </div>
                </div>
                <div>
                  <div className="text-slate-400 text-[0.65rem] sm:text-xs uppercase tracking-wider mb-1">{t('positions.openPositions')}</div>
                  <div className="text-lg sm:text-xl font-bold text-white">
                    {activeCount}
                  </div>
                  <div className="text-[0.65rem] text-slate-500 mt-1">
                    {t('positions.openPositionsLabel')}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between">
               <div>
                 <div className="text-slate-500 text-[0.65rem] sm:text-xs uppercase tracking-wider mb-1">{t('positions.historicalWinRate')}</div>
                 <div className="text-lg sm:text-xl font-bold text-amber-400">
                   {settledStats.win_rate != null ? `${settledStats.win_rate.toFixed(1)}%` : '-'}
                 </div>
               </div>
               <div className="flex gap-2">
                 <span className="px-2 py-1 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/10 text-[0.65rem] font-bold">
                   {settledStats.total_wins} WIN
                 </span>
                 <span className="px-2 py-1 rounded-md bg-rose-500/10 text-rose-400 border border-rose-500/10 text-[0.65rem] font-bold">
                   {settledStats.total_losses} LOSS
                 </span>
               </div>
            </div>
          </div>

          {/* PnL Stats Grid (Span 7) - 垂直排列 */}
          <div className="lg:col-span-7 grid grid-cols-2 gap-2 sm:gap-3">
            {[
              {
                label: t('positions.yesterdayPnl'),
                value: pnlStats.yesterday,
                desc: t('positions.yesterday'),
                icon: (
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )
              },
              {
                label: t('positions.weekPnl'),
                value: pnlStats.week,
                desc: t('positions.thisWeek'),
                icon: (
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605" />
                  </svg>
                )
              },
              {
                label: t('positions.monthPnl'),
                value: pnlStats.month,
                desc: t('positions.thisMonth'),
                icon: (
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                  </svg>
                )
              },
              {
                label: t('positions.allTimePnl'),
                value: pnlStats.allTime,
                desc: t('positions.allTime'),
                icon: (
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                  </svg>
                )
              },
            ].map((stat, idx) => (
              <div key={idx} className="p-3 sm:p-4 rounded-xl bg-slate-900/40 border border-slate-800/60 backdrop-blur-sm flex flex-col justify-center hover:bg-slate-800/50 transition-colors hover:border-slate-700/80">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {stat.icon}
                    <span className="text-slate-400 text-[0.65rem] sm:text-xs">{stat.desc}</span>
                  </div>
                </div>
                <div className={`text-xl sm:text-2xl font-bold ${stat.value >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {formatUsd(stat.value, { withSign: true })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Positions Section with Wallet Filter */}
        <div className="rounded-2xl border border-slate-800/60 bg-slate-900/30 overflow-hidden">
          {/* Section Header with Wallet Selector */}
          <div className="p-4 sm:p-5 border-b border-slate-800/60">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
              {/* Title */}
              <div className="flex items-baseline gap-3 shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-white">
                  {t('positions.positionsDetail')}
                </h2>
                <span className="text-xs sm:text-sm font-medium text-slate-500 bg-slate-800/50 px-2 py-0.5 rounded-md border border-slate-700/50">
                  {totalPositions}
                </span>
              </div>

              {/* Wallet Selector - Primary Filter */}
              {wallets.length > 1 && (
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 lg:mb-0 lg:hidden">
                    <span className="text-xs text-slate-500">{t('positions.selectWallet')}</span>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide -mx-1 px-1">
                    {wallets.map((wallet, index) => {
                      const isActive = index === activeWallet;

                      return (
                        <button
                          key={wallet.wallet_address}
                          onClick={() => {
                            walletSelectionTouchedRef.current = true;
                            setActiveWallet(index);
                          }}
                          className={`
                            relative flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-all duration-200 shrink-0
                            ${isActive
                              ? 'bg-sky-500/10 border-sky-500/40 shadow-sm'
                              : 'bg-slate-800/40 border-slate-700/40 hover:bg-slate-800/60 hover:border-slate-600/50'
                            }
                          `}
                        >
                          {/* Wallet Icon */}
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isActive ? 'bg-sky-500/20' : 'bg-slate-700/50'}`}>
                            <svg className={`w-4 h-4 ${isActive ? 'text-sky-400' : 'text-slate-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3" />
                            </svg>
                          </div>

                          {/* Wallet Name */}
                          <div className="flex items-center gap-1.5">
                            <span className={`text-xs font-semibold ${isActive ? 'text-sky-300' : 'text-slate-300'}`}>
                              Wallet {index + 1}
                            </span>
                            {wallet.is_trading_wallet && (
                              <span className="text-[0.55rem] bg-sky-500 text-white px-1 py-0.5 rounded font-bold">
                                MAIN
                              </span>
                            )}
                            {isActive && (
                              <span className="w-1.5 h-1.5 rounded-full bg-sky-400"></span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Secondary Filter Tabs */}
            <div className="mt-4 pt-4 border-t border-slate-800/40">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <span className="text-xs text-slate-500 shrink-0">{t('positions.filterByStatus')}</span>

                {/* Desktop Filter Tabs */}
                <div className="hidden sm:flex p-0.5 bg-slate-800/50 rounded-lg">
                  {[
                    { key: 'all', label: t('positions.all'), count: totalPositions },
                    { key: 'active', label: t('positions.statusActive'), count: activeCount },
                    { key: 'unredeemed', label: t('positions.statusUnredeemed'), count: unredeemedCount },
                    { key: 'settled', label: t('positions.statusSettled'), count: settledCount },
                  ].map((tab) => {
                    const isTabActive = positionFilter === tab.key;
                    return (
                      <button
                        key={tab.key}
                        onClick={() => setPositionFilter(tab.key)}
                        className={`
                          relative px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200
                          ${isTabActive ? 'text-white' : 'text-slate-500 hover:text-slate-300'}
                        `}
                      >
                        {isTabActive && (
                          <div className="absolute inset-0 bg-slate-700 rounded-md -z-10 shadow-sm"></div>
                        )}
                        <span className="flex items-center gap-1.5">
                          {tab.label}
                          <span className={`text-[0.65rem] py-0.5 px-1 rounded transition-colors ${isTabActive ? 'bg-slate-600 text-white' : 'bg-slate-800 text-slate-600'}`}>
                            {tab.count}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Mobile Filter Dropdown */}
                <div className="sm:hidden relative">
                  <select
                    value={positionFilter}
                    onChange={(e) => setPositionFilter(e.target.value)}
                    className="appearance-none w-full bg-slate-800/80 border border-slate-700 text-slate-300 text-sm rounded-lg px-3 py-2 pr-8 focus:outline-none focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/30"
                  >
                    <option value="all">{t('positions.all')} ({totalPositions})</option>
                    <option value="active">{t('positions.statusActive')} ({activeCount})</option>
                    <option value="unredeemed">{t('positions.statusUnredeemed')} ({unredeemedCount})</option>
                    <option value="settled">{t('positions.statusSettled')} ({settledCount})</option>
                  </select>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Positions List Content */}
          <div className="p-4 sm:p-5">
            {/* Positions List Header - Desktop */}
        {allPositions.length > 0 && (
          <div className="hidden lg:flex items-center gap-4 px-4 py-2 text-[0.65rem] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-800/50">
            <span className="w-8">#</span>
            <span className="w-16 shrink-0">{t('positions.outcome')}</span>
            <span className="flex-1">{t('positions.market')}</span>
            <div className="flex items-center gap-6 shrink-0">
              <span className="w-16 text-right">{t('positions.positionSize')}</span>
              <span className="w-16 text-right">{t('positions.avgPrice')}</span>
              <span className="w-20 text-right">{t('positions.settledValue')}</span>
              <span className="w-24 text-right">{t('positions.pnl')}</span>
            </div>
            <span className="w-24 text-right">{t('positions.status')}</span>
            <span className="w-24 text-right">{t('positions.action')}</span>
          </div>
        )}

        {/* Positions List */}
        {allPositions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 rounded-3xl border-2 border-dashed border-slate-800 bg-slate-900/20">
            <div className="w-16 h-16 mb-4 rounded-full bg-slate-800/50 flex items-center justify-center text-3xl">👻</div>
            <p className="text-slate-400 font-medium">{t('positions.emptyWalletTitle')}</p>
            <p className="text-sm text-slate-600 mt-1">{t('positions.emptyWalletDesc')}</p>
          </div>
        ) : (
          <div className="min-h-[200px] -mx-3 px-3 overflow-x-auto scrollbar-hide">
            {filteredPositions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 rounded-3xl border border-slate-800 bg-slate-900/20">
                <p className="text-slate-500">
                  {positionFilter === 'active' && t('positions.noOpen')}
                  {positionFilter === 'unredeemed' && t('positions.noUnredeemed')}
                  {positionFilter === 'settled' && t('positions.noSettled')}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-2 min-w-max">
                {/* Current Positions with Header */}
                {filteredPositions.some(({ type }) => type !== 'historical') && (
                  <>
                    {/* Mobile Header for Current Positions */}
                    <div className="flex lg:hidden items-center gap-3 px-3 py-2 text-[0.6rem] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-800/50">
                      <span className="w-6">#</span>
                      <span className="w-16 shrink-0">{t('positions.outcome')}</span>
                      <span className="flex-1 min-w-[120px]">{t('positions.market')}</span>
                      <div className="flex items-center gap-4 shrink-0">
                        <span className="w-14 text-right">{t('positions.positionSize')}</span>
                        <span className="w-14 text-right">{t('positions.avgPrice')}</span>
                        <span className="w-16 text-right">{t('positions.currentValue')}</span>
                        <span className="w-20 text-right">{t('positions.pnl')}</span>
                      </div>
                      <span className="w-auto sm:w-28 text-right">{t('positions.status')}</span>
                      <span className="w-16 sm:w-20 text-right">{t('positions.action')}</span>
                    </div>
                    {filteredPositions
                      .filter(({ type }) => type !== 'historical')
                      .map(({ position, order, type }) => renderPositionRow(position, order, type))}
                  </>
                )}
                {/* Historical Positions with Header */}
                {filteredPositions.some(({ type }) => type === 'historical') && (
                  <>
                    {/* Mobile Header for Historical Positions */}
                    <div className="flex lg:hidden items-center gap-3 px-3 py-2 text-[0.6rem] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-800/50">
                      <span className="w-6">#</span>
                      <span className="w-16 shrink-0">{t('positions.outcome')}</span>
                      <span className="flex-1 min-w-[120px]">{t('positions.market')}</span>
                      <div className="flex items-center gap-4 shrink-0">
                        <span className="w-14 text-right">{t('positions.positionSize')}</span>
                        <span className="w-14 text-right">{t('positions.avgPrice')}</span>
                        <span className="w-16 text-right">{t('positions.settledValue')}</span>
                        <span className="w-20 text-right">{t('positions.pnl')}</span>
                      </div>
                      <span className="w-auto sm:w-24 text-right">{t('positions.status')}</span>
                      <span className="w-16 sm:w-24 text-right">{t('positions.action')}</span>
                    </div>
                    {filteredPositions
                      .filter(({ type }) => type === 'historical')
                      .map(({ position, order }) => renderHistoricalPositionRow(position, order))}
                  </>
                )}
              </div>
            )}
          </div>
        )}
          </div>
        </div>
        <Modal
          open={sellConfirmOpen}
          onClose={handleCloseSellConfirm}
          title={t('positions.sellConfirmTitle')}
          subtitle={t('positions.sellConfirmSubtitle')}
          maxWidthClass="max-w-[560px]"
        >
          {sellTarget ? (
            <div className="space-y-4">
              <div className="p-4 rounded-2xl border border-slate-800/60 bg-slate-900/50">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-slate-100 font-semibold truncate">
                      {sellTarget.title || t('positions.marketUnknown')}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                      <span className="px-2 py-0.5 rounded-full border border-slate-700/60 bg-slate-800/60 text-slate-200 font-semibold uppercase">
                        {sellTarget.outcome_emoji ? `${sellTarget.outcome_emoji} ` : ''}{sellTarget.outcome}
                      </span>
                      <span>
                        {t('positions.sellConfirmSize')}: <span className="text-slate-200 font-mono">{(sellTarget.size ?? 0).toFixed(4)}</span>
                      </span>
                      <span>
                        {t('positions.sellConfirmWallet')}: <span className="text-slate-200 font-mono">{formatAddress(sellTarget.wallet_address || currentWallet?.wallet_address)}</span>
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-semibold ${sellTargetPnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {formatUsd(sellTargetPnl, { withSign: true })}
                    </div>
                    <div className="text-[0.65rem] text-slate-500">{t('positions.pnlFloating')}</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="p-3 rounded-xl border border-slate-800/60 bg-slate-900/40">
                  <div className="text-slate-500">{t('positions.avgPrice')}</div>
                  <div className="text-slate-100 font-mono">${sellTarget.avg_price?.toFixed(3) ?? '-'}</div>
                </div>
                <div className="p-3 rounded-xl border border-slate-800/60 bg-slate-900/40">
                  <div className="text-slate-500">{t('positions.currentPrice')}</div>
                  <div className="text-slate-100 font-mono">${sellTarget.cur_price?.toFixed(3) ?? '-'}</div>
                </div>
                <div className="p-3 rounded-xl border border-slate-800/60 bg-slate-900/40">
                  <div className="text-slate-500">{t('positions.currentValue')}</div>
                  <div className="text-slate-100 font-semibold">{formatUsd(sellTarget.value)}</div>
                </div>
                <div className="p-3 rounded-xl border border-slate-800/60 bg-slate-900/40">
                  <div className="text-slate-500">{t('positions.investedCost')}</div>
                  <div className="text-slate-100 font-semibold">{formatUsd(sellTarget.initial_value)}</div>
                </div>
              </div>

              <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-xs text-amber-200">
                {t('positions.sellConfirmTip')}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={handleCloseSellConfirm}
                  disabled={isSellingTarget}
                  className="px-4 py-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors text-sm font-medium border border-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleConfirmSell}
                  disabled={isSellingTarget}
                  className="px-5 py-2 rounded-xl bg-rose-500/15 hover:bg-rose-500/25 text-rose-400 hover:text-rose-300 text-sm font-bold transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 border border-rose-500/30"
                >
                  {isSellingTarget ? (
                    <span className="w-4 h-4 border-2 border-rose-400/30 border-t-rose-400 rounded-full animate-spin" />
                  ) : null}
                  {isSellingTarget ? t('positions.sellPreparing') : t('positions.sellConfirmAction')}
                </button>
              </div>
            </div>
          ) : null}
        </Modal>
      </div>
    </section>
  );
}
